import { useState, useEffect, useCallback } from 'react';

/**
 * BeforeInstallPromptEvent is not yet in TypeScript's lib.dom.d.ts
 * So we define our own interface
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Storage keys
const VISIT_COUNT_KEY = 'mnm_pwa_visit_count';
const DISMISS_TIMESTAMP_KEY = 'mnm_pwa_dismiss_timestamp';
const INSTALLED_KEY = 'mnm_pwa_installed';

// Configuration
const MIN_VISITS_BEFORE_PROMPT = 3;
const DISMISS_COOLDOWN_DAYS = 7;

interface UseInstallPromptOptions {
  /** When false, skips visit counting and event listeners. Default: true */
  enabled?: boolean;
}

/**
 * Hook to manage PWA install prompt
 * Shows install prompt after user engagement (3+ visits)
 * Respects dismissal cooldown (7 days)
 */
export function useInstallPrompt({ enabled = true }: UseInstallPromptOptions = {}) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState<boolean>(false);
  const [canInstall, setCanInstall] = useState<boolean>(false);
  const [shouldShowPrompt, setShouldShowPrompt] = useState<boolean>(false);
  const [visitCount, setVisitCount] = useState<number>(0);

  // Check if app is already installed
  const checkIfInstalled = useCallback((): boolean => {
    // Check if running in standalone mode (installed PWA)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    // Check iOS standalone mode
    if ((window.navigator as { standalone?: boolean }).standalone === true) {
      return true;
    }
    // Check localStorage flag
    if (localStorage.getItem(INSTALLED_KEY) === 'true') {
      return true;
    }
    return false;
  }, []);

  // Check if dismiss cooldown has passed
  const isDismissCooldownPassed = useCallback((): boolean => {
    const dismissTimestamp = localStorage.getItem(DISMISS_TIMESTAMP_KEY);
    if (!dismissTimestamp) {
      return true;
    }
    const dismissDate = new Date(parseInt(dismissTimestamp, 10));
    const cooldownEnd = new Date(dismissDate.getTime() + DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000);
    return new Date() > cooldownEnd;
  }, []);

  // Increment visit count
  const incrementVisitCount = useCallback((): number => {
    const currentCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    const newCount = currentCount + 1;
    localStorage.setItem(VISIT_COUNT_KEY, newCount.toString());
    return newCount;
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (!enabled) return;

    // Check if already installed
    if (checkIfInstalled()) {
      setIsInstalled(true);
      return;
    }

    // Increment and get visit count
    const count = incrementVisitCount();
    setVisitCount(count);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);

      // Check if we should show the prompt
      if (count >= MIN_VISITS_BEFORE_PROMPT && isDismissCooldownPassed()) {
        setShouldShowPrompt(true);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setShouldShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem(INSTALLED_KEY, 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [enabled, checkIfInstalled, incrementVisitCount, isDismissCooldownPassed]);

  // Handle install
  const handleInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShouldShowPrompt(false);

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
        localStorage.setItem(INSTALLED_KEY, 'true');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  }, [deferredPrompt]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setShouldShowPrompt(false);
    localStorage.setItem(DISMISS_TIMESTAMP_KEY, Date.now().toString());
  }, []);

  // Force show prompt (for manual trigger, e.g., from settings)
  const showPrompt = useCallback(() => {
    if (canInstall && deferredPrompt && !isInstalled) {
      setShouldShowPrompt(true);
    }
  }, [canInstall, deferredPrompt, isInstalled]);

  return {
    /** Whether the app can be installed (prompt is available) */
    canInstall,
    /** Whether the app is already installed */
    isInstalled,
    /** Whether we should show the install prompt UI */
    shouldShowPrompt,
    /** Current visit count */
    visitCount,
    /** Trigger the native install prompt */
    handleInstall,
    /** Dismiss the prompt (starts cooldown) */
    handleDismiss,
    /** Manually show the prompt (if available) */
    showPrompt,
    /** The deferred prompt event (for advanced usage) */
    deferredPrompt,
  };
}

export default useInstallPrompt;
